/**
 * 🚀🌌 LA PETITE ÉPICERIE - LANDING PAGE GALACTIQUE
 * 
 * La plus épique épicerie spatiale de l'univers !
 * Design "Dinguerie Spatiale" - MODE MOZART/JOBS/TESLA ACTIVÉ
 * 
 * Sections:
 * 1. Hero - Station Spatiale avec logo flottant
 * 2. CTAs Principaux - Actions rapides (Livraison en 1er !)
 * 3. Coffre des Flyers - Flyers cachés avec capture email
 * 4. Livraison - Max le livreur + Copywriting Press
 * 5. Produits - Grille Bento avec SONS
 * 6. Témoignages - Avis Google 5 étoiles
 * 7. Commandants - Momo & Jean-Phi + Histoire
 * 8. Social & Contact - Snap + Teaser mystérieux
 * 9. Footer
 */

import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

// Composants Landing
import {
  SpaceBackground,
  HeroSection,
  MainCTAsSection,
  FlyerVaultSection,
  GammeEtonnanteSection,
  ProductsShowcaseSection,
  DeliverySection,
  TestimonialsSection,
  CommandantsSection,
  SocialContactSection,
  HeartbeatsTeaserSection,
  AnniversaryFlyer
} from '../components/landing';

// Footer existant
import Footer from '../components/Footer';

// Séparateur spatial entre sections
const SpaceDivider = () => (
  <div className="relative py-8">
    <motion.div
      className="absolute left-1/2 -translate-x-1/2 w-px h-full"
      style={{
        background: 'linear-gradient(to bottom, transparent, rgba(6, 182, 212, 0.3), transparent)'
      }}
    />
    <motion.div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-500/50"
      animate={{
        scale: [1, 1.5, 1],
        opacity: [0.5, 1, 0.5]
      }}
      transition={{ duration: 2, repeat: Infinity }}
    />
  </div>
);

export default function LandingPage() {
  const contentRef = useRef(null);

  const scrollToContent = () => {
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Toast notifications */}
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#0f172a',
            color: '#fff',
            border: '1px solid rgba(6, 182, 212, 0.3)',
            borderRadius: '12px',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <div 
        className="min-h-screen relative overflow-x-hidden overflow-y-auto"
        style={{
          // 🔧 FIX DÉFINITIF Chrome Mobile / Samsung Galaxy Z Flip
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
        }}
      >
        {/* Background Spatial Animé */}
        <SpaceBackground enableWarp={false} intensity="high" />

        {/* Contenu Principal */}
        <div className="relative z-10">
          {/* 🎂 BANNER ANNIVERSAIRE - 1 AN ! (Tout en haut) */}
          <AnniversaryFlyer variant="banner" />

          {/* SECTION 1: Hero */}
          <HeroSection onScrollDown={scrollToContent} />

          {/* Point d'ancrage pour le scroll */}
          <div ref={contentRef} />

          {/* SECTION 2: CTAs Principaux (Livraison en premier !) */}
          <MainCTAsSection />

          <SpaceDivider />

          {/* SECTION 3: Restez Connectés - Rejoignez la Communauté (juste après -10%) */}
          <SocialContactSection />

          <SpaceDivider />

          {/* SECTION 4: Coffre Spatial des Flyers */}
          <FlyerVaultSection />

          <SpaceDivider />

          {/* SECTION 5: Livraison avec Max */}
          <DeliverySection />

          <SpaceDivider />

          {/* SECTION 6: Produits (Rayons avec sons !) */}
          <ProductsShowcaseSection />

          <SpaceDivider />

          {/* SECTION 7: Gamme Étonnante - Produits avec SONS ! */}
          <GammeEtonnanteSection />

          <SpaceDivider />

          {/* SECTION 8: Témoignages Google */}
          <TestimonialsSection />

          <SpaceDivider />

          {/* SECTION 9: Heartbeats - Le Secret de l'Épicerie 💜 */}
          <HeartbeatsTeaserSection />

          <SpaceDivider />

          {/* SECTION 10: Commandants - Momo & Jean-Phi + Télécharger App + Station Active */}
          <CommandantsSection />

          <SpaceDivider />

          {/* SECTION 11: Footer */}
          <Footer />
        </div>

        {/* Pixie est déjà dans App.js - pas besoin de la dupliquer ici */}

        {/* HUD Corners - Décoration globale */}
        <div className="fixed top-0 left-0 w-32 h-32 pointer-events-none z-40 opacity-30">
          <div className="absolute top-4 left-4 w-16 h-px bg-gradient-to-r from-cyan-500/50 to-transparent" />
          <div className="absolute top-4 left-4 h-16 w-px bg-gradient-to-b from-cyan-500/50 to-transparent" />
        </div>
        <div className="fixed top-0 right-0 w-32 h-32 pointer-events-none z-40 opacity-30">
          <div className="absolute top-4 right-4 w-16 h-px bg-gradient-to-l from-cyan-500/50 to-transparent" />
          <div className="absolute top-4 right-4 h-16 w-px bg-gradient-to-b from-cyan-500/50 to-transparent" />
        </div>
        <div className="fixed bottom-0 left-0 w-32 h-32 pointer-events-none z-40 opacity-30">
          <div className="absolute bottom-4 left-4 w-16 h-px bg-gradient-to-r from-purple-500/50 to-transparent" />
          <div className="absolute bottom-4 left-4 h-16 w-px bg-gradient-to-t from-purple-500/50 to-transparent" />
        </div>
        <div className="fixed bottom-0 right-0 w-32 h-32 pointer-events-none z-40 opacity-30">
          <div className="absolute bottom-4 right-4 w-16 h-px bg-gradient-to-l from-purple-500/50 to-transparent" />
          <div className="absolute bottom-4 right-4 h-16 w-px bg-gradient-to-t from-purple-500/50 to-transparent" />
        </div>
      </div>
    </>
  );
}
